import React, { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { RefreshCw, Save, Download, Search } from 'lucide-react';

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = parseFloat(v);
  return isNaN(n) ? '' : n % 1 === 0 ? n.toString() : n.toFixed(1);
};

const MonthlyGrid2 = ({ user }) => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [costCentre, setCostCentre] = useState('all');
  const [costCentres, setCostCentres] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [syncInfo, setSyncInfo] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const monthBounds = (m) => { const [y, mo] = m.split('-').map(Number); return [`${m}-01`, `${m}-${String(new Date(y, mo, 0).getDate()).padStart(2, '0')}`]; };
  const [fromDate, setFromDate] = useState(() => monthBounds(month)[0]);
  const [toDate, setToDate] = useState(() => monthBounds(month)[1]);
  const [unmatched, setUnmatched] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/monthly-grid-2/data?month=${month}&cost_centre=${costCentre}&from_date=${fromDate}&to_date=${toDate}`);
      const d = res.data;
      setRows(d.rows || []);
      setCostCentres(d.cost_centres || []);
      setUnmatched(d.unmatched_shiftpro || []);
      setSyncInfo({
        shiftpro: d.shiftpro_count || 0,
        mps: d.mps_count || 0,
        time: d.synced_at || '',
      });
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [month, costCentre, fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[idx] };
      row[field] = parseFloat(value) || 0;
      // Recalculate DOP
      row.dop = Math.round(((row.days_present || 0) + (row.weekly_offs || 0) + (row.paid_leaves || 0) - (row.lwp || 0) + (row.odw_days || 0)) * 10) / 10;
      row._edited = true;
      if (['days_present', 'weekly_offs', 'paid_leaves', 'ot_hours', 'odw_days'].includes(field)) row._att_edited = true;
      next[idx] = row;
      return next;
    });
  };

  const updateComment = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value, _edited: true };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const edited = rows.filter(r => r._att_edited).length;
      const res = await api.post('/monthly-grid-2/save', { month, rows, from_date: fromDate, to_date: toDate });
      toast.success(`Saved ${res.data.saved} records${edited ? ` — ${edited} edited row(s) updated in ShiftPro Attendance` : ''}`);
      setTimeout(fetchData, 3000);
    } catch (err) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const headers = ['Employee', 'Code', 'Cost Centre', 'Designation', 'Per Day', 'Shift', 'Present', 'W.Off', 'PL', 'LWP', 'D.O.P.', 'OT Hrs', 'ODW', 'Incentive', 'T.A.', 'Advance', 'Deductions', 'Source'];
    const csvRows = [headers.join(',')];
    filtered.forEach(r => {
      csvRows.push([
        `"${r.employee_name}"`, r.employee_code, `"${r.cost_centre}"`, `"${r.designation}"`,
        r.per_day_rate, r.shift_hours, r.days_present, r.weekly_offs, r.paid_leaves, r.lwp,
        r.dop, r.ot_hours, r.odw_days, r.incentive, r.ta, r.advance, r.deductions, r.source
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `monthly-grid-2-${month}.csv`; a.click();
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  let filtered = rows.filter(r =>
    !search || (r.employee_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.employee_code || '').toLowerCase().includes(search.toLowerCase())
  );

  if (sortKey) {
    filtered = [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  const totals = filtered.reduce((acc, r) => ({
    days_present: acc.days_present + (r.days_present || 0),
    weekly_offs: acc.weekly_offs + (r.weekly_offs || 0),
    paid_leaves: acc.paid_leaves + (r.paid_leaves || 0),
    lwp: acc.lwp + (r.lwp || 0),
    dop: acc.dop + (r.dop || 0),
    ot_hours: acc.ot_hours + (r.ot_hours || 0),
    odw_days: acc.odw_days + (r.odw_days || 0),
    incentive: acc.incentive + (r.incentive || 0),
    ta: acc.ta + (r.ta || 0),
    advance: acc.advance + (r.advance || 0),
    deductions: acc.deductions + (r.deductions || 0),
  }), { days_present: 0, weekly_offs: 0, paid_leaves: 0, lwp: 0, dop: 0, ot_hours: 0, odw_days: 0, incentive: 0, ta: 0, advance: 0, deductions: 0 });

  const months = [];
  for (let i = -3; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) });
  }

  const SortHeader = ({ label, field, className = '' }) => (
    <th className={`px-2 py-2.5 text-center cursor-pointer hover:bg-slate-200/50 select-none whitespace-nowrap ${className}`}
      onClick={() => handleSort(field)}>
      {label} {sortKey === field ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  const d = <span className="text-slate-300">–</span>;
  const editableCell = (rowIdx, field, color = '') => {
    const val = filtered[rowIdx]?.[field] ?? '';
    const realIdx = rows.findIndex(r => r.employee_id === filtered[rowIdx]?.employee_id);
    return (
      <input type="number" step="0.5" min="0" value={val}
        onChange={e => updateRow(realIdx, field, e.target.value)}
        className={`w-14 text-center text-xs py-0.5 border border-slate-200 rounded font-mono focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 ${color}`}
        data-testid={`grid2-${field}-${rowIdx}`}
      />
    );
  };

  return (
    <div className="space-y-4" data-testid="monthly-grid-2-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Monthly Grid 2</h1>
          <p className="text-sm text-slate-500">Auto-populated from ShiftPro Attendance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading} data-testid="sync-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Syncing...' : 'Fetch / Sync'}
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm" data-testid="export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export
          </Button>
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-all-btn">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap bg-white/80 backdrop-blur-md rounded-xl border border-slate-200/60 p-4 shadow-sm">
        <div>
          <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Month</label>
          <select value={month} onChange={e => { const [f, t] = monthBounds(e.target.value); setMonth(e.target.value); setFromDate(f); setToDate(t); }}
            className="block mt-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:border-indigo-400"
            data-testid="month-select">
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">From Date</label>
          <input type="date" value={fromDate} onChange={e => e.target.value && setFromDate(e.target.value)}
            className="block mt-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:border-indigo-400" data-testid="grid2-from-date" />
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">To Date</label>
          <input type="date" value={toDate} onChange={e => e.target.value && setToDate(e.target.value)}
            className="block mt-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:border-indigo-400" data-testid="grid2-to-date" />
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Cost Centre</label>
          <select value={costCentre} onChange={e => setCostCentre(e.target.value)}
            className="block mt-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:border-indigo-400"
            data-testid="cc-select">
            <option value="all">All Cost Centres</option>
            {costCentres.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Search</label>
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Name or Code..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:border-indigo-400"
              data-testid="grid2-search"
            />
          </div>
        </div>
      </div>

      {/* Sync Status */}
      {syncInfo && (
        <div className="flex items-center gap-3 text-xs text-slate-500" data-testid="sync-status">
          <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
            {syncInfo.shiftpro} from ShiftPro
          </span>
          <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
            {syncInfo.mps} existing in MPS
          </span>
          {unmatched.length > 0 && (
            <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-medium" title={unmatched.join(', ')} data-testid="grid2-unmatched">
              {unmatched.length} in ShiftPro not found in MPS Employees: {unmatched.slice(0, 5).join(', ')}{unmatched.length > 5 ? '…' : ''}
            </span>
          )}
          <span className="text-slate-400">
            Synced: {syncInfo.time ? new Date(syncInfo.time).toLocaleString('en-IN') : '–'}
          </span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-slate-500">Fetching from ShiftPro...</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden" data-testid="grid2-table">
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-2 py-2.5 text-center w-8">#</th>
                  <SortHeader label="Employee" field="employee_name" className="text-left min-w-[160px] sticky left-0 bg-slate-50 z-20" />
                  <th className="px-2 py-2.5 text-center">Desig.</th>
                  <SortHeader label="Per Day" field="per_day_rate" />
                  <th className="px-2 py-2.5 text-center">Shift</th>
                  <th className="px-2 py-2.5 text-center border-l border-slate-200 bg-emerald-50/50" colSpan={8}>Attendance</th>
                  <th className="px-2 py-2.5 text-center border-l border-slate-200 bg-indigo-50/50" colSpan={2}>Extra</th>
                  <th className="px-2 py-2.5 text-center border-l border-slate-200 bg-amber-50/50" colSpan={4}>Financial (₹)</th>
                  <th className="px-2 py-2.5 text-center border-l border-slate-200">Source</th>
                </tr>
                <tr className="text-[10px] font-semibold text-slate-600 border-b border-slate-200">
                  <th></th>
                  <th className="sticky left-0 bg-slate-50 z-20"></th>
                  <th></th>
                  <th className="px-1 py-1.5 text-center">₹</th>
                  <th className="px-1 py-1.5 text-center">Hrs</th>
                  <SortHeader label="Present" field="days_present" className="border-l border-slate-200 bg-emerald-50/50" />
                  <SortHeader label="W.Off" field="weekly_offs" className="bg-emerald-50/50" />
                  <SortHeader label="PL" field="paid_leaves" className="bg-emerald-50/50" />
                  <SortHeader label="LWP" field="lwp" className="bg-emerald-50/50" />
                  <SortHeader label="Absent" field="absent" className="bg-emerald-50/50" />
                  <SortHeader label="Half" field="half_days" className="bg-emerald-50/50" />
                  <SortHeader label="MP" field="missed_punch" className="bg-emerald-50/50" />
                  <SortHeader label="D.O.P." field="dop" className="bg-emerald-50/50 font-bold" />
                  <SortHeader label="OT Hrs" field="ot_hours" className="border-l border-slate-200 bg-indigo-50/50" />
                  <SortHeader label="ODW" field="odw_days" className="bg-indigo-50/50" />
                  <th className="px-1 py-1.5 text-center border-l border-slate-200 bg-amber-50/50">Incentive</th>
                  <th className="px-1 py-1.5 text-center bg-amber-50/50">T.A.</th>
                  <th className="px-1 py-1.5 text-center bg-amber-50/50">Advance</th>
                  <th className="px-1 py-1.5 text-center bg-amber-50/50">Deduct.</th>
                  <th className="px-1 py-1.5 text-center border-l border-slate-200"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r, idx) => (
                  <tr key={r.employee_id} className={`hover:bg-slate-50 ${r._edited ? 'bg-yellow-50/40' : ''}`} data-testid={`grid2-row-${idx}`}>
                    <td className="px-2 py-2 text-center text-slate-400 font-mono">{idx + 1}</td>
                    <td className="px-2 py-2 text-left sticky left-0 bg-white z-10 group-hover:bg-slate-50" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.06)' }}>
                      <div className="font-semibold text-slate-800 whitespace-nowrap">{r.employee_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{r.employee_code}</div>
                    </td>
                    <td className="px-2 py-2 text-center text-slate-600 whitespace-nowrap">{r.designation || d}</td>
                    <td className="px-2 py-2 text-right font-mono text-slate-600">{fmt(r.per_day_rate) || d}</td>
                    <td className="px-2 py-2 text-center font-mono text-slate-500">{r.shift_hours}h</td>
                    <td className="px-1 py-1 text-center border-l border-slate-100">{editableCell(idx, 'days_present')}</td>
                    <td className="px-1 py-1 text-center">{editableCell(idx, 'weekly_offs')}</td>
                    <td className="px-1 py-1 text-center">{editableCell(idx, 'paid_leaves')}</td>
                    <td className="px-1 py-1 text-center">{editableCell(idx, 'lwp')}</td>
                    <td className="px-2 py-2 text-center font-mono text-red-600" data-testid={`grid2-absent-${idx}`}>{fmt(r.absent) || 0}</td>
                    <td className="px-2 py-2 text-center font-mono text-orange-600" data-testid={`grid2-half-${idx}`}>{fmt(r.half_days) || 0}</td>
                    <td className="px-2 py-2 text-center font-mono text-amber-600" title="Missed punch, pending approval" data-testid={`grid2-mp-${idx}`}>{fmt(r.missed_punch) || 0}</td>
                    <td className="px-2 py-2 text-center font-mono font-bold text-emerald-700 bg-emerald-50/30">{fmt(r.dop)}</td>
                    <td className="px-1 py-1 text-center border-l border-slate-100">{editableCell(idx, 'ot_hours')}</td>
                    <td className="px-1 py-1 text-center">{editableCell(idx, 'odw_days')}</td>
                    <td className="px-1 py-1 text-center border-l border-slate-100">{editableCell(idx, 'incentive')}</td>
                    <td className="px-1 py-1 text-center">{editableCell(idx, 'ta')}</td>
                    <td className="px-1 py-1 text-center">{editableCell(idx, 'advance')}</td>
                    <td className="px-1 py-1 text-center">{editableCell(idx, 'deductions')}</td>
                    <td className="px-2 py-2 text-center border-l border-slate-100">
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                        r.source === 'shiftpro' ? 'bg-violet-100 text-violet-700' :
                        r.source === 'mps' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {r.source === 'shiftpro' ? 'SP' : r.source === 'mps' ? 'MPS' : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 z-10">
                <tr className="bg-slate-100 font-semibold text-xs border-t-2 border-slate-300">
                  <td className="px-2 py-2.5"></td>
                  <td className="px-2 py-2.5 sticky left-0 bg-slate-100 z-10 font-bold">TOTAL ({filtered.length})</td>
                  <td></td><td></td><td></td>
                  <td className="px-2 py-2.5 text-center font-mono border-l border-slate-200">{fmt(totals.days_present)}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(totals.weekly_offs)}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(totals.paid_leaves)}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(totals.lwp)}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(filtered.reduce((a, r) => a + (r.absent || 0), 0))}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(filtered.reduce((a, r) => a + (r.half_days || 0), 0))}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(filtered.reduce((a, r) => a + (r.missed_punch || 0), 0))}</td>
                  <td className="px-2 py-2.5 text-center font-mono font-bold text-emerald-700">{fmt(totals.dop)}</td>
                  <td className="px-2 py-2.5 text-center font-mono border-l border-slate-200">{fmt(totals.ot_hours)}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(totals.odw_days)}</td>
                  <td className="px-2 py-2.5 text-center font-mono border-l border-slate-200">{fmt(totals.incentive)}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(totals.ta)}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(totals.advance)}</td>
                  <td className="px-2 py-2.5 text-center font-mono">{fmt(totals.deductions)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyGrid2;
